import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/PixiPlugin";
import * as PIXI from "pixi.js";
import { Container, Graphics, Sprite } from "pixi.js";
import { getSymbol } from "../../config/getSymbol";
import { isSuperQuickBtnOnGlobal, landscapeLogoPositions, landscapeMeterPositions, portraitLogoPositions, portraitMeterPositions, RespinIndex } from "../../config/GlobalVariableConfig";
import { REEL_BG_POS_ALL, REEL_DIVIDER, REEL_FRAME, REEL_NUMBERS, REEL_SCALE_LANDSCAPE, REEL_SCALE_PORTRAIT, REEL_WIDTH, REEL_Y_POS_LAND, REEL_Y_POS_PORT, setVisibleSymbols, SHOWN_REELS_HEIGHT, SYMBOL_HEIGHT, SYMBOL_WIDTH, SYMBOLS_PER_REEL, VISIBLE_SYMBOLS_PER_REEL } from "../../config/reelConfig";
import { DisplayComponent } from "../../core/displays/DisplayComponent";
import { Manager } from "../../core/Manager";
import { DummyReelSymbol, ReelObject, reelSpinEmitter, SymbolObject } from "../../core/reels/ReelInit";
import { Reels } from "../../core/reels/Reels";
import { clearPools, getPooledSpineSymbol, releaseSymbolToPool } from "../../core/Symbols";
import { EventConstants } from "../../gameClientEvents/EventConstants";
import { GameConstants } from "../../gameClientEvents/GameConstants";
import { spinData } from "../../server/getServerResposesFun";
import { LogoComponent } from "../logo/LogoComponent";
import { MeterComponent } from "../meter/MeterComponent";

gsap.registerPlugin(PixiPlugin);
PixiPlugin.registerPIXI(PIXI);

export class SAReels extends Reels {

    private maskTimeline: gsap.core.Timeline | null = null;
    override paylineYOffset = -850;

    protected override initReels(): void {
        this.prepareContainers();

        const reelRowVisibleCount = VISIBLE_SYMBOLS_PER_REEL.toString();
        const configObj = REEL_BG_POS_ALL.find(obj => obj.hasOwnProperty(reelRowVisibleCount));
        const config = configObj ? configObj[reelRowVisibleCount as keyof typeof configObj] : null;

        for (let i = 0; i < REEL_NUMBERS; i++) {
            const reelContainer = this.createReelContainer(i);
            const reelSymbolIds = this.getInitialSymbolIds(i);

            this.reelSymbolsBeforeSpin.push(reelSymbolIds);

            const reelObj: ReelObject = {
                reelIndex: i,
                container: reelContainer,
                symbols: [],
            };

            for (let j = 0; j < SYMBOLS_PER_REEL + 1; j++) {
                const symbolId = reelSymbolIds[j === SYMBOLS_PER_REEL ? j - 1 : j];
                const symbolObj = this.createSymbol(i, j, symbolId);
                reelObj.symbols.push(symbolObj);
                reelContainer.addChild(symbolObj.view);
            }

            reelContainer.y = config.reelY;
            reelContainer.x = config.reelX + (i * (REEL_WIDTH + REEL_DIVIDER));
            reelContainer.label = `Reel_${i}`;

            this.allReelContainer.addChild(reelContainer);
            this.allReelArray.push(reelObj);

            this.setupAnticipation(i);
        }

        this.applyReelMask();

        this.position.set(GameConstants.BACKGROUND_SIZE / 2, GameConstants.BACKGROUND_SIZE / 2);

        this.finalizeReelSetup();
    }

    protected override setWinLineSpineObj() {
        super.setWinLineSpineObj();
        this.winLinesSPine.position.set(638, 550);
        this.toggleWinLinesSpine.position.set(638, 550);
    }

    protected prepareContainers(): void {
        this.reelOverlayContainer.destroy(true);
        this.toggleOverlayContainer.destroy(true);
    }

    protected createReelContainer(i: number): Container {
        const container = new Container();
        container.x = REEL_FRAME + i * (REEL_WIDTH + REEL_DIVIDER);
        container.y = REEL_FRAME;
        return container;
    }

    protected getInitialSymbolIds(i: number): number[] {
        if (RespinIndex.value !== 0) {
            return spinData.respins[RespinIndex.value - 2].reelSymbols[i];
        }
        const reelRand = Math.floor(Math.random() * (DummyReelSymbol[i].length - SYMBOLS_PER_REEL));
        return DummyReelSymbol[i].slice(reelRand, reelRand + SYMBOLS_PER_REEL + 1);
    }

    protected createSymbol(reelIndex: number, rowIndex: number, symbolId: number): SymbolObject {
        console.log("reelIndex " + reelIndex);
        const symbolContainer = new Container();
        const symbol = new Sprite(getSymbol(symbolId));

        symbol.anchor = 0.5;
        symbolContainer.addChild(symbol);

        if (rowIndex === SYMBOLS_PER_REEL) {
            symbolContainer.y = -(SYMBOL_HEIGHT / 2); // The "hidden" top symbol
        } else {
            symbolContainer.y = (SYMBOL_HEIGHT / 2) + rowIndex * SYMBOL_HEIGHT;
        }
        symbolContainer.x = Math.round(SYMBOL_WIDTH / 2);

        return {
            symbolIndex: rowIndex,
            id: symbolId,
            y: symbolContainer.y,
            view: symbolContainer,
            symbol: symbol,
        };
    }

    protected setupAnticipation(i: number): void {
        this.anticipation[i] = Spine.from({
            skeleton: "anticipationSkel",
            atlas: "anticipationAtlas",
        });

        this.anticipation[i].x = 50 + REEL_FRAME + REEL_WIDTH / 2 + i * (REEL_WIDTH + REEL_DIVIDER);
        this.anticipation[i].y = -260 + REEL_FRAME + SHOWN_REELS_HEIGHT / 2;
    }

    protected applyReelMask(): void {
        const visibleCount = VISIBLE_SYMBOLS_PER_REEL;
        const totalHeight = SYMBOL_HEIGHT * visibleCount;

        const configObj = REEL_BG_POS_ALL.find(obj => obj.hasOwnProperty(visibleCount.toString()));
        const config = configObj ? configObj[(visibleCount.toString()) as keyof typeof configObj] : null;

        const visPos = SYMBOLS_PER_REEL - VISIBLE_SYMBOLS_PER_REEL;
        const topY = (SYMBOL_HEIGHT * visPos) - Math.abs(config.reelY);

        const reelMask = new Graphics()
            .rect(REEL_FRAME, REEL_FRAME, REEL_WIDTH * REEL_NUMBERS + (REEL_NUMBERS - 1) * REEL_DIVIDER, totalHeight)
            .fill(0x950a5a);

        reelMask.y = topY;
        reelMask.x = config.reelX;
        reelMask.label = "reelMask";

        this.allReelContainer.addChild(reelMask);
        this.allReelContainer.mask = reelMask;
        this.addChild(this.allReelContainer);
    }

    protected finalizeReelSetup(): void {
        this.initReelOverlayContainer();
        for (let i = 0; i < REEL_NUMBERS; i++) {
            this.addReelSymbolsToContainer(i);
        }
    }

    protected applyOverlayReelMask(): void {
        const visibleCount = VISIBLE_SYMBOLS_PER_REEL;
        const totalHeight = SYMBOL_HEIGHT * visibleCount;

        const configObj = REEL_BG_POS_ALL.find(obj => obj.hasOwnProperty(visibleCount.toString()));
        const config = configObj ? configObj[(visibleCount.toString()) as keyof typeof configObj] : null;

        const visPos = SYMBOLS_PER_REEL - VISIBLE_SYMBOLS_PER_REEL + 1;
        const topY = config.reelY + visPos * SYMBOL_HEIGHT;

        const reelMask = new Graphics()
            .rect(-1000, REEL_FRAME, (REEL_WIDTH * 4) * REEL_NUMBERS + (REEL_NUMBERS - 1) * REEL_DIVIDER, totalHeight)
            .fill(0x950a5a);

        reelMask.y = topY;
        reelMask.x = config.reelX;
        reelMask.label = "reelMaskOverlay";

        this.reelOverlayContainer.addChild(reelMask);
        this.reelOverlayContainer.mask = reelMask;
    }

    public override addReelSymbolsToContainer(reelNum: number) {
        const reelObj = this.allReelArray[reelNum];

        const visibleCount = VISIBLE_SYMBOLS_PER_REEL.toString();
        const configObj = REEL_BG_POS_ALL.find(obj => obj.hasOwnProperty(visibleCount));
        const config = configObj ? configObj[visibleCount as keyof typeof configObj] : null;

        if (config) {
            for (let posNum = 0; posNum < SYMBOLS_PER_REEL; posNum++) {
                const symbolCont = this.reelSymbolContainers[reelNum][posNum];
                symbolCont.y = config.reelY + Math.round(SYMBOL_HEIGHT / 2) + posNum * SYMBOL_HEIGHT;
            }
        }

        const visPos = SYMBOLS_PER_REEL - VISIBLE_SYMBOLS_PER_REEL;

        for (let posNum = 0; posNum < SYMBOLS_PER_REEL; posNum++) {
            const prevSymbol = this.reelSymbols[reelNum][posNum];
            if (prevSymbol) {
                this.reelSymbolContainers[reelNum][posNum].removeChild(prevSymbol);
                // prevSymbol.destroy();
                releaseSymbolToPool(prevSymbol);
                this.reelSymbols[reelNum][posNum] = null;
            }
            else {
                const symbolId = reelObj.symbols[posNum].id;
                //const symbol = getSpineSymbol(symbolId);
                const symbol = getPooledSpineSymbol(symbolId);
                this.reelSymbolContainers[reelNum][posNum].addChild(symbol);
                this.reelSymbols[reelNum][posNum] = symbol;
                if (symbolId === GameConstants.BLOCKED_SYMBOL_ID) {
                    this.reelSymbolContainers[reelNum][posNum].visible = false;
                }
                this.reelSymbolContainers[reelNum][posNum].visible = posNum >= visPos;
            }

            this.reelSymbols[reelNum][posNum].state.setAnimation(0, "land", false);
        }

        // Defer visibility change to the next frame
        requestAnimationFrame(() => {
            reelObj.container.visible = false;
        });
    }

    public override resetReelOverlayContainer() {
        reelSpinEmitter.emit(EventConstants.OVERLAY_SYMBOLS_RESET);
        for (let reelNum = 0; reelNum < REEL_NUMBERS; reelNum++) {
            for (let posNum = 0; posNum < SYMBOLS_PER_REEL; posNum++) {

                const prevSymbol = this.reelSymbols[reelNum][posNum];
                if (prevSymbol) {
                    this.reelSymbolContainers[reelNum][posNum].removeChild(prevSymbol);
                    // prevSymbol.destroy();
                    releaseSymbolToPool(prevSymbol);
                    this.reelSymbols[reelNum][posNum] = null;
                }
            }
        }

    }

    public async animateMask(heights?: number, skipAnimation: boolean = false, onComplete?: () => void) {
        if (this.maskTimeline) {
            this.maskTimeline.kill();
            this.maskTimeline = null;
        }

        if (heights) {
            setVisibleSymbols(heights);
        }

        // this.showOverlaySymbols();

        const speedMultiplier = isSuperQuickBtnOnGlobal ? 5.5 : 1;
        const reelVisibleCount = VISIBLE_SYMBOLS_PER_REEL.toString();
        const configObj = REEL_BG_POS_ALL.find(obj => obj.hasOwnProperty(reelVisibleCount));
        const config = configObj ? configObj[reelVisibleCount as keyof typeof configObj] : null;


        const animDuration = 1;
        const animEase = "power2.inOut";

        if (!skipAnimation) {
            this.maskTimeline = gsap.timeline({
                defaults: { duration: animDuration, ease: animEase, overwrite: true },
                onComplete: () => {
                    const mask2 = this.reelOverlayContainer.getChildByName("reelMaskOverlay") as Graphics;
                    if (mask2) {
                        this.reelOverlayContainer.mask = null;
                        mask2.destroy();
                    }
                    if (onComplete) onComplete();
                }
            });
            this.maskTimeline.timeScale(speedMultiplier);
        }

        if (config) {
            const parentCont = this.allReelContainer.parent as Container;
            let orientationScale = (Manager.Orientation === "LANDSCAPE") ? REEL_SCALE_LANDSCAPE[heights ? heights - 3 : 0] : REEL_SCALE_PORTRAIT[heights ? heights - 3 : 0];
            const parentProps = {
                pixi: {
                    scale: orientationScale,
                },
                duration: animDuration,
                ease: animEase
            };

            const reelFrameCont = parentCont.parent as Container;
            let orientationYPos = (Manager.Orientation === "LANDSCAPE") ? REEL_Y_POS_LAND[heights ? heights - 3 : 0] : REEL_Y_POS_PORT[heights ? heights - 3 : 0];
            const reelFrameContProps = {
                y: orientationYPos,
                duration: animDuration,
                ease: animEase
            };

            const meterCont = DisplayComponent.get(MeterComponent).getInstance();
            let orientationPos = (Manager.Orientation === "LANDSCAPE") ? landscapeMeterPositions()[heights - 3][heights] : portraitMeterPositions[heights - 3][heights];
            const meterContProps = {
                x: orientationPos.posX,
                y: orientationPos.posY,
                duration: animDuration,
                ease: animEase
            };

            const logoCont = DisplayComponent.get(LogoComponent).getInstance();
            let orientationLogoPos = (Manager.Orientation === "LANDSCAPE") ? landscapeLogoPositions()[heights - 3][heights] : portraitLogoPositions[heights - 3][heights];
            const logoContProps = {
                x: orientationLogoPos.posX,
                y: orientationLogoPos.posY,
                duration: animDuration,
                ease: animEase
            };

            if (skipAnimation) {
                if (parentCont) gsap.set(parentCont, parentProps);
                if (reelFrameCont) gsap.set(reelFrameCont, reelFrameContProps);
                if (meterCont) gsap.set(meterCont, meterContProps);
                if (logoCont) gsap.set(logoCont, logoContProps);
            } else {
                if (parentCont) this.maskTimeline!.to(parentCont, parentProps, 0);
                if (reelFrameCont) this.maskTimeline!.to(reelFrameCont, reelFrameContProps, 0);
                if (meterCont) this.maskTimeline!.to(meterCont, meterContProps, 0);
                if (logoCont) this.maskTimeline!.to(logoCont, logoContProps, 0);
            }
        }

        const container = this.allReelContainer;
        let reelMask = container.getChildByName("reelMask") as Graphics;
        let reelMask2 = this.reelOverlayContainer.getChildByName("reelMaskOverlay") as Graphics;

        const visibleCount = VISIBLE_SYMBOLS_PER_REEL;
        const targetHeight = SYMBOL_HEIGHT * visibleCount;
        const visPos = SYMBOLS_PER_REEL - VISIBLE_SYMBOLS_PER_REEL;

        const topY = (SYMBOL_HEIGHT * visPos) - Math.abs(config.reelY);
        const topY2 = config.reelY + (SYMBOL_HEIGHT * visPos);

        const maskProps = { pixi: { height: targetHeight, y: topY } };
        const maskProps2 = { pixi: { y: topY2 } };

        if (skipAnimation) {
            gsap.set(reelMask, maskProps);
            gsap.set(reelMask2, maskProps2);
            if (onComplete) onComplete();
        } else {
            this.maskTimeline!.to(reelMask, maskProps, 0);
            this.maskTimeline!.to(reelMask2, maskProps2, 0);
        }
    }

    public showOverlaySymbols() {
        const visPos = SYMBOLS_PER_REEL - VISIBLE_SYMBOLS_PER_REEL;
        this.applyOverlayReelMask();
        for (let reelNum = 0; reelNum < REEL_NUMBERS; reelNum++) {
            const reelObj = this.allReelArray[reelNum];
            for (let posNum = 0; posNum < SYMBOLS_PER_REEL; posNum++) {
                const symbolId = reelObj.symbols[posNum].id;
                if (symbolId === GameConstants.BLOCKED_SYMBOL_ID) {
                    this.reelSymbolContainers[reelNum][posNum].visible = false;
                }
                this.reelSymbolContainers[reelNum][posNum].visible = posNum >= visPos;
            }
        }
    }

    public applyMaskSizeOnly(height: number): void {
        setVisibleSymbols(height);

        const reelVisibleCount = VISIBLE_SYMBOLS_PER_REEL.toString();
        const configObj = REEL_BG_POS_ALL.find(obj => obj.hasOwnProperty(reelVisibleCount));
        const config = configObj ? configObj[reelVisibleCount as keyof typeof configObj] : null;
        if (!config) return;

        // 1. Snap main reel mask
        const reelMask = this.allReelContainer.getChildByName("reelMask") as Graphics;
        const visPos = SYMBOLS_PER_REEL - VISIBLE_SYMBOLS_PER_REEL;
        const targetHeight = SYMBOL_HEIGHT * VISIBLE_SYMBOLS_PER_REEL;
        const topY = (SYMBOL_HEIGHT * visPos) - Math.abs(config.reelY);
        if (reelMask) gsap.set(reelMask, { pixi: { height: targetHeight, y: topY } });

        // 2. Snap parent container scale
        const parentCont = this.allReelContainer.parent as Container;
        const orientationScale = (Manager.Orientation === "LANDSCAPE")
            ? REEL_SCALE_LANDSCAPE[height - 3]
            : REEL_SCALE_PORTRAIT[height - 3];
        if (parentCont) gsap.set(parentCont, { pixi: { scale: orientationScale } });

        // 3. Snap reel frame Y position
        const reelFrameCont = parentCont?.parent as Container;
        const orientationYPos = (Manager.Orientation === "LANDSCAPE")
            ? REEL_Y_POS_LAND[height - 3]
            : REEL_Y_POS_PORT[height - 3];
        if (reelFrameCont) gsap.set(reelFrameCont, { y: orientationYPos });

        // 4. Snap meter and logo positions
        const meterCont = DisplayComponent.get(MeterComponent).getInstance();
        const orientationPos = (Manager.Orientation === "LANDSCAPE")
            ? landscapeMeterPositions()[height - 3][height]
            : portraitMeterPositions[height - 3][height];
        if (meterCont) gsap.set(meterCont, { x: orientationPos.posX, y: orientationPos.posY });

        const logoCont = DisplayComponent.get(LogoComponent).getInstance();
        const orientationLogoPos = (Manager.Orientation === "LANDSCAPE")
            ? landscapeLogoPositions()[height - 3][height]
            : portraitLogoPositions[height - 3][height];
        if (logoCont) gsap.set(logoCont, { x: orientationLogoPos.posX, y: orientationLogoPos.posY });
    }

    private pendingMaskHeight: number | null = null;

    public setPendingMaskHeight(height: number): void {
        this.pendingMaskHeight = height;
        setVisibleSymbols(height);
    }

    public applyPendingMaskOnSpinStart(): void {
        if (this.pendingMaskHeight === null) return;
        const height = this.pendingMaskHeight;
        this.pendingMaskHeight = null;
        this.animateMask(height, true, () => {
            this.showOverlaySymbols();
        });
    }

    public override async startReels(RespinIndex: number, isNewSpin: boolean, spinStype: string): Promise<void> {
        // Snap mask to upgraded size BEFORE reels start moving
        if (this.pendingMaskHeight !== null) {
            const height = this.pendingMaskHeight;
            this.pendingMaskHeight = null;
            this.applyMaskSizeOnly(height);
        }

        await super.startReels(RespinIndex, isNewSpin, spinStype);
    }


    public override destroy(options?: any) {
        clearPools();
        super.destroy(options);
    }
}
